import React, {
  useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react'
import { isInViewport } from '@usap/core'
import {
  Box, ResponsiveBox, ResponsiveText, ShowIfDesktop, ShowIfNotDesktop, useResponsiveWrapperContext,
} from '@usap/uicomponents'
import {
  TextWithPointer, ShipOrderOptions, PopUpLayer, FreeShipping,
} from '@usap/widgets'
import localConfig from '../../../../../config/local-config'
import captchConfig from '../../../../../config/captcha-config'
import { GTM } from '../../../../helpers'
import { getSyncSEDetails } from '../../../../helpers/getShippingExpectationDate'
import { SearchContext } from '../../../common/context/SearchContextProvider'

const { siteKey } = captchConfig
const { covidMessage } = localConfig.featureFlag || {}
const trueValue = true

const SeeAllDates = ({
  setShipNotify,
  setActivePopUpSku,
  data,
  dataTestId = null,
}) => {
  const { sku } = data || {}

  const seeAllDateClick = useCallback(() => {
    setShipNotify(true)
    setActivePopUpSku(sku)
  }, [sku, setShipNotify, setActivePopUpSku])

  const fxMargin = useMemo(() => ({ large: { left: 'xsmall' }, other: '0px' }), [])

  return (
    <Box>
      <TextWithPointer
        color="blueLink"
        weight={400}
        size="small"
        onClick={seeAllDateClick}
        fxMargin={fxMargin}
        data-testid={dataTestId}
      >
        {' See All Dates'}
      </TextWithPointer>
    </Box>
  )
}

export default function ShippingExpInfo(props) {
  const {
    setGroundDeliveryTime,
    index,
    data,
    invDetails,
    cityName,
    zipCode,
    zipCodeHandler,
    zipcodeError,
    isLoading,
    getDeliveryDate,
    isShowHeight,
    pageType,
    isLocationUpdated,
    setIsLocationUpdated
  } = props

  const { searchState: { products, isSearch } } = useContext(SearchContext)
  const { selectedVehicle = {} } = products
  const { id: invId, sku } = data
  const { isFreeShipping = false } = invDetails[`${invId}`] || {}

  const {
    getGroundDeliveryTime,
    productImpressions,
  }= GTM

  const { isDesktop }= useResponsiveWrapperContext()
  const isImpressionFired = useRef(false)
  const [shipNotify, setShipNotify] = useState(false)
  const [shipNotifyForm] = useState(false)
  const [activePopUpSku, setActivePopUpSku] = useState(0)
  const [locationDetails, setLocationDetails] = useState({})
  const [locationData, setLocationData] = useState(null)
  const invObj = invDetails[`${invId}`]

  const utcDate = locationDetails && locationDetails[`${sku}`] 
    ? locationDetails[`${sku}`].deliveryDates
        && locationDetails[`${sku}`].deliveryDates.length
        && locationDetails[`${sku}`].deliveryDates[0].formattedDate
    : 'none'

  let eventListName = 'Category Product Listing'

  if (isSearch) {
    eventListName = 'Product Search'
  }
  if (pageType === 'mntsrp') {
    eventListName= 'Maintenance Recommendations'
  }

  const getSyncSEDetailsThen = useCallback(
    seData => {
      const dlvrTime = getGroundDeliveryTime(seData, sku)
      setLocationDetails(seData)
      setGroundDeliveryTime(dlvrTime)
      return dlvrTime
    },
    [getGroundDeliveryTime, setGroundDeliveryTime, sku, locationData],
  )

  const getSyncSEDetailsFinally = useCallback(
    (dlvrTime = null) => {
      isImpressionFired.current = true
      const impressionsInfo = {
        ...data,
        vehicle: selectedVehicle,
        groundDeliveryTime: dlvrTime,
        inventory: invObj || invDetails,
      }
      productImpressions(
        eventListName,
        [impressionsInfo],
      )
    },
    [data, eventListName, invDetails, invObj, productImpressions, selectedVehicle, locationData],
  )

  const handleScroll = useCallback(() => {
    const cardElem = document.getElementById(`productTitle-${sku}-${index}`)
    if (cardElem) {
      const isExist = isInViewport(cardElem, 100)
      let dlvrTime = null

      if (isExist) {
        // eslint-disable-next-line no-use-before-define
        unSubscribe()
        getSyncSEDetails(data, false, ['Ground'])
          .then(seData => {
            dlvrTime = getSyncSEDetailsThen(seData)
          })
          .finally(() => {
            getSyncSEDetailsFinally(dlvrTime)
          })
      }
    }
  }, [
    data,
    getSyncSEDetailsFinally,
    getSyncSEDetailsThen,
    index,
    sku,
    locationData,
    // eslint-disable-next-line no-use-before-define
    unSubscribe
  ])

  const subscribe = useCallback(
    () => {
      if (__BROWSER__) {
        window.addEventListener('scroll', handleScroll, true)
        window.addEventListener('touchmove', handleScroll, true)
        window.addEventListener('wheel', handleScroll, true)
      }
    },
    [handleScroll],
  )

  const unSubscribe = useCallback(
    () => {
      if (__BROWSER__) {
        window.removeEventListener('scroll', handleScroll, true)
        window.removeEventListener('touchmove', handleScroll, true)
        window.removeEventListener('wheel', handleScroll, true)
      }
    },
    [handleScroll],
  )

  useEffect(() => {
    const hasInvDetails = Object.keys(invDetails).length

    const enableProductImp = __BROWSER__
        && zipCode
        && !isImpressionFired.current
        && hasInvDetails
    if (enableProductImp && index < 3) {
      let dlvrTime = null

      getSyncSEDetails(data, false, ['Ground'])
        .then(seData => {
          dlvrTime = getSyncSEDetailsThen(seData)
        })
        .finally(() => {
          getSyncSEDetailsFinally(dlvrTime)
        })
    } else if (enableProductImp && index >= 3) {
      subscribe()
    }
    return () => {
      unSubscribe()
    }
  }, [
    zipCode,
    invDetails,
    index,
    data,
    getSyncSEDetailsThen,
    getSyncSEDetailsFinally,
    subscribe,
    unSubscribe,
    locationData
  ])

  useEffect(() => {
    // When shipping address is changed, update the expetced delivery dates of each product 
    if(zipCode && isLocationUpdated) {
      isImpressionFired.current = false
      setLocationData({
        updated: true
      })
      // Update context
      setIsLocationUpdated(false)
    }
  }, [zipCode, isLocationUpdated])


  return (
    <>
      <PopUpLayer
        flag={shipNotify}
        closePopUp={setShipNotify}
        covidCookie={covidMessage}
        PopUpComponent={ShipOrderOptions}
        siteKey={siteKey}
        shipNotifyForm={shipNotifyForm}
        shipDetails={locationDetails[data.sku]}
        zipCode={zipCode}
        cityName={cityName}
        zipCodeHandler={zipCodeHandler}
        isLoading={isLoading}
        zipcodeError={zipcodeError}
        activePopUpSku={activePopUpSku}
        serpFlag={trueValue}
        getDeliveryDate={getDeliveryDate}
        index={index}
      />

      <Box
        height={isDesktop && isShowHeight ? '28px':'auto'}
        pad={{ top: '4px' }}
      >
        <FreeShipping sku={sku} isFreeShipping={isFreeShipping} />
      </Box>

      <Box
        height={
         utcDate === 'none' || isDesktop
           ? { min: isDesktop ? '36px' : '46px' }
           : 'auto'
      }
      >
        {utcDate && utcDate !== 'none' && invObj && invObj.available ? (
          <ResponsiveBox
            fxDirection={{ large: 'row', other: 'column' }}
            fxMargin={{ large: { vertical: 'small' }, other: { top: isFreeShipping ? '-4px' :'small' } }}
          >
            <Box direction="row">
              <ResponsiveText
                fxSize={{ large: '16px', other: 'small' }}
                color="dark-2"
              >
                Get it by
              </ResponsiveText>
              <ResponsiveText
                fxSize={{ large: '16px', other: 'small' }}
                color="dark-2"
                weight="bold"
                margin={{ horizontal: 'xsmall' }}
              >
                {utcDate}
              </ResponsiveText>
            </Box>
            <ShowIfDesktop>
              <SeeAllDates setShipNotify={setShipNotify} setActivePopUpSku={setActivePopUpSku} data={data} dataTestId="desktop-see-all-dates-serp" />
            </ShowIfDesktop>
            <ShowIfNotDesktop>
              <SeeAllDates setShipNotify={setShipNotify} setActivePopUpSku={setActivePopUpSku} data={data} dataTestId="wom-see-all-dates-serp" />
            </ShowIfNotDesktop>
          </ResponsiveBox>
        )
          : ''}
      </Box>
    </>
  )
}
